import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon,
  UserGroupIcon,
  PaperAirplaneIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { formatDateTime } from '../../utils/dateUtils';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  type: 'announcement' | 'notification' | 'marketing' | 'system';
}

interface EmailHistory {
  id: string;
  subject: string;
  recipientCount: number;
  sentAt: any;
  status: 'sent' | 'failed' | 'pending';
  template?: string;
  sentBy: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: 'student' | 'employer';
  verified?: boolean;
  suspended?: boolean;
}

const EmailManagement: React.FC = () => {
  const { hasPermission } = useAdmin();
  const [activeTab, setActiveTab] = useState<'compose' | 'templates' | 'history'>('compose');
  
  // Compose email state
  const [emailForm, setEmailForm] = useState({
    subject: '',
    content: '',
    recipientType: 'all' as 'all' | 'students' | 'employers' | 'specific',
    specificRecipients: [] as string[],
    useTemplate: false,
    selectedTemplate: '',
    sendImmediately: true,
    scheduledDate: ''
  });

  // Users and templates state
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [emailHistory, setEmailHistory] = useState<EmailHistory[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState<'all' | 'students' | 'employers' | 'verified' | 'suspended'>('all');

  useEffect(() => {
    if (hasPermission('communications', 'read')) {
      fetchUsers();
      fetchTemplates();
      fetchEmailHistory();
    }
  }, [hasPermission]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, userFilter]);

  const fetchUsers = async () => {
    try {
      const response = await adminApiService.getAllUsers({ limit: 1000 });
      setUsers(response.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await adminApiService.getEmailTemplates();
      setTemplates(response || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load email templates');
      setTemplates([]);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      const response = await adminApiService.getEmailHistory();
      setEmailHistory(response || []);
    } catch (error) {
      console.error('Failed to fetch email history:', error);
      toast.error('Failed to load email history');
      setEmailHistory([]);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    switch (userFilter) {
      case 'students':
        filtered = filtered.filter(user => user.userType === 'student');
        break;
      case 'employers':
        filtered = filtered.filter(user => user.userType === 'employer');
        break;
      case 'verified':
        filtered = filtered.filter(user => user.verified);
        break;
      case 'suspended':
        filtered = filtered.filter(user => user.suspended);
        break;
    }

    setFilteredUsers(filtered);
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEmailForm({
        ...emailForm,
        selectedTemplate: templateId,
        subject: template.subject,
        content: template.content,
        useTemplate: true
      });
    }
  };

  const getRecipientCount = () => {
    switch (emailForm.recipientType) {
      case 'all':
        return users.length;
      case 'students':
        return users.filter(u => u.userType === 'student').length;
      case 'employers':
        return users.filter(u => u.userType === 'employer').length;
      case 'specific':
        return selectedUsers.size;
      default:
        return 0;
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.content.trim()) {
      toast.error('Please fill in subject and content');
      return;
    }

    const recipientCount = getRecipientCount();
    if (recipientCount === 0) {
      toast.error('No recipients selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to send this email to ${recipientCount} recipient(s)?`)) {
      return;
    }

    setLoading(true);
    try {
      await adminApiService.sendBulkEmail({
        subject: emailForm.subject,
        content: emailForm.content,
        recipientType: emailForm.recipientType,
        specificRecipients: emailForm.recipientType === 'specific' ? Array.from(selectedUsers) : undefined,
        templateId: emailForm.useTemplate ? emailForm.selectedTemplate : undefined,
        scheduledDate: emailForm.sendImmediately ? undefined : emailForm.scheduledDate
      });

      toast.success(`Email sent successfully to ${recipientCount} recipient(s)!`);
      
      // Reset form
      setEmailForm({
        subject: '',
        content: '',
        recipientType: 'all',
        specificRecipients: [],
        useTemplate: false,
        selectedTemplate: '',
        sendImmediately: true,
        scheduledDate: ''
      });
      setSelectedUsers(new Set());
      
      // Refresh history
      fetchEmailHistory();
      
    } catch (error: any) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const renderComposeTab = () => (
    <div className="space-y-6">
      {/* Template Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Email Template</h3>
        <div className="flex items-center space-x-4 mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={emailForm.useTemplate}
              onChange={(e) => setEmailForm({...emailForm, useTemplate: e.target.checked})}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Use template</span>
          </label>
        </div>
        
        {emailForm.useTemplate && (
          <select
            value={emailForm.selectedTemplate}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">Select a template...</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name} ({template.type})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Email Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Email Content</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
            <input
              type="text"
              value={emailForm.subject}
              onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Enter email subject..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
            <textarea
              value={emailForm.content}
              onChange={(e) => setEmailForm({...emailForm, content: e.target.value})}
              rows={8}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Enter email content... Use {firstName}, {lastName}, {email} for personalization"
            />
            <p className="mt-2 text-sm text-gray-500">
              Available variables: {'{firstName}'}, {'{lastName}'}, {'{email}'}, {'{userType}'}
            </p>
          </div>
        </div>
      </div>

      {/* Recipients */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recipients</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="all"
                checked={emailForm.recipientType === 'all'}
                onChange={(e) => setEmailForm({...emailForm, recipientType: e.target.value as any})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">All Users ({users.length})</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="students"
                checked={emailForm.recipientType === 'students'}
                onChange={(e) => setEmailForm({...emailForm, recipientType: e.target.value as any})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Students ({users.filter(u => u.userType === 'student').length})</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="employers"
                checked={emailForm.recipientType === 'employers'}
                onChange={(e) => setEmailForm({...emailForm, recipientType: e.target.value as any})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Employers ({users.filter(u => u.userType === 'employer').length})</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="specific"
                checked={emailForm.recipientType === 'specific'}
                onChange={(e) => setEmailForm({...emailForm, recipientType: e.target.value as any})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Specific Users ({selectedUsers.size})</span>
            </label>
          </div>

          {/* Specific Users Selection */}
          {emailForm.recipientType === 'specific' && (
            <div className="mt-4 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-900">Select Users</h4>
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  {selectedUsers.size === filteredUsers.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              {/* Search and Filter */}
              <div className="flex space-x-4 mb-4">
                <div className="flex-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Search users..."
                  />
                </div>
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value as any)}
                  className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="all">All Users</option>
                  <option value="students">Students</option>
                  <option value="employers">Employers</option>
                  <option value="verified">Verified</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              
              {/* User List */}
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded">
                {filteredUsers.map(user => (
                  <label
                    key={user.id}
                    className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={() => handleUserSelect(user.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {`${user.firstName} ${user.lastName}`.trim() || 'No Name'}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.userType === 'student' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {user.userType}
                          </span>
                          {user.verified && (
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          )}
                          {user.suspended && (
                            <XMarkIcon className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Send Options */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Send Options</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="sendTiming"
                checked={emailForm.sendImmediately}
                onChange={() => setEmailForm({...emailForm, sendImmediately: true})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Send immediately</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="radio"
                name="sendTiming"
                checked={!emailForm.sendImmediately}
                onChange={() => setEmailForm({...emailForm, sendImmediately: false})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Schedule for later</span>
            </label>
          </div>
          
          {!emailForm.sendImmediately && (
            <input
              type="datetime-local"
              value={emailForm.scheduledDate}
              onChange={(e) => setEmailForm({...emailForm, scheduledDate: e.target.value})}
              min={new Date().toISOString().slice(0, 16)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => setPreviewMode(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <EyeIcon className="h-4 w-4 mr-2" />
          Preview
        </button>
        
        <div className="flex space-x-3">
          <button
            disabled={loading}
            onClick={handleSendEmail}
            className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                Send to {getRecipientCount()} recipient(s)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Email History</h3>
        <p className="text-sm text-gray-500">Track all emails sent from the admin panel</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subject
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Recipients
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sent At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sent By
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {emailHistory.map((email) => (
              <tr key={email.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{email.subject}</div>
                    {email.template && (
                      <div className="text-xs text-gray-500">Template: {email.template}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {email.recipientCount} users
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    email.status === 'sent' ? 'bg-green-100 text-green-800' :
                    email.status === 'failed' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {email.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDateTime(email.sentAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {email.sentBy}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {emailHistory.length === 0 && (
          <div className="text-center py-12">
            <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No emails sent</h3>
            <p className="mt-1 text-sm text-gray-500">
              Email history will appear here once you start sending emails.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  if (!hasPermission('communications', 'read')) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
            <XMarkIcon className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to access email management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Management</h1>
            <p className="text-gray-600">Send emails to users and manage communication</p>
          </div>
          <div className="flex items-center space-x-2">
            <EnvelopeIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-500">{users.length} total users</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'compose', name: 'Compose Email', icon: PaperAirplaneIcon },
            { id: 'history', name: 'Email History', icon: ClockIcon }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'compose' && renderComposeTab()}
      {activeTab === 'history' && renderHistoryTab()}

      {/* Preview Modal */}
      {previewMode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Email Preview</h3>
              <button 
                onClick={() => setPreviewMode(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="border-b border-gray-200 pb-4 mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">{emailForm.subject}</h4>
                  <p className="text-sm text-gray-500">To: {getRecipientCount()} recipient(s)</p>
                </div>
                
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700">
                    {emailForm.content.replace(/\{firstName\}/g, 'John').replace(/\{lastName\}/g, 'Doe').replace(/\{email\}/g, 'john.doe@example.com')}
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <p>This email was sent from Workazon Admin Panel</p>
                  <p>© {new Date().getFullYear()} Workazon. All rights reserved.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setPreviewMode(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Close Preview
              </button>
              <button
                onClick={() => {
                  setPreviewMode(false);
                  handleSendEmail();
                }}
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailManagement;